/**
 * The floating launcher: a 56px solid terracotta disc (the `primary` token)
 * pinned to the bottom-right, carrying the off-white Alfie mark.
 *
 * The disc is opaque in BOTH themes. The earlier glass disc (white at 72%)
 * vanished on the light theme's beige page, where the fill and the white
 * border both matched the ground and only the shadow drew the edge. A filled
 * primary disc with a light mark is what every chat launcher does, and it
 * holds its contrast over any page, in either theme.
 *
 * On desktop it stays visible while the panel is open and swaps its mark for
 * an X. On mobile the panel is a full-height sheet, so the launcher hides
 * rather than floating on top of it.
 *
 * LAYERING (changed 6 Oct). The disc is z-[52] and the panel z-[54] over a
 * z-[53] scrim — ABOVE the ordinary dialog layer (z-50), below the mobile nav
 * (`Layout` z-[60]/z-[70]), destructive confirms (`alert-dialog` z-[80]) and
 * the full-screen modals at z-[100].
 *
 * It was z-[25], deliberately below every drawer and dialog, so that "an open
 * surface always covers the launcher rather than the other way round". Tim
 * reversed that: Alfie pulses to get your attention, so it must be reachable
 * while a dialog is open — and raising the disc alone would only open its panel
 * BEHIND that dialog, so the whole widget moved up together.
 *
 * It stays below alert-dialog on purpose. A floating disc over a "delete this?"
 * confirm can cover the buttons, and no pulse is worth that.
 */
import { useEffect, useRef, useState } from 'react';
import { GripVertical, X } from 'lucide-react';

import HearthstoneIcon from '@/components/icons/HearthstoneIcon';
import { useIsMobile } from '@/hooks/use-mobile';

/** Clearance between an avoided composer and the launcher above it. */
export const AVOID_GAP_PX = 12;

export const DESKTOP_INSET_PX = 20;
export const MOBILE_INSET_PX = 16;

/**
 * How the toggle shortcut is spelled on this machine. `userAgentData.platform`
 * is the modern signal ("macOS"); `navigator.platform` ("MacIntel") is the
 * fallback browsers that lack it still ship. Read once — the keyboard a page is
 * loaded on does not change mid-session.
 */
export function shortcutLabel(): string {
  if (typeof navigator === 'undefined') return 'Ctrl+J';
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ||
    navigator.platform ||
    '';
  return /mac|iphone|ipad|ipod/i.test(platform) ? '\u2318J' : 'Ctrl+J';
}

const SHORTCUT_LABEL = shortcutLabel();

/**
 * §M Liquid Glass for the hover label: the fill, border and blur all come from
 * the tokens in globals.css so light/dark and any future retune stay in one
 * place. The `-webkit-` twin is not optional — Safari still ships the prefix
 * only. The disc itself is solid and does not use this.
 */
const GLASS_BACKDROP = {
  backdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
  WebkitBackdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
} as const;

interface AlfieLauncherProps {
  isOpen: boolean;
  onToggle: () => void;
  /** From `useLauncherBottom`: px above the viewport bottom, pre safe-area. */
  bottomPx: number;
  /** A reply is streaming while the widget is shut — pulses the halo ring. */
  isAnswering?: boolean;
  /** A reply landed while the widget was shut — shows the unread dot. */
  hasNewReply?: boolean;
  /**
   * The current page. A moved disc goes back to its corner when this changes,
   * and on a reload, because the moved spot is never saved.
   */
  positionKey?: string;
}

/** Touch: how long a press is held before the move handle shows. */
export const MOVE_HOLD_MS = 500;
/** Mouse: how long the pointer rests on the disc before the move handle shows. */
export const MOVE_HOVER_MS = 1500;
/** The handle hides after this long with no drag. */
export const MOVE_IDLE_MS = 4000;
/** A finger that travels this far before the hold fires is scrolling, not holding. */
const HOLD_SLOP_PX = 10;
/** The handle: a glass grip parked this far left of the disc. */
const HANDLE_W_PX = 28;
const HANDLE_H_PX = 44;
const HANDLE_GAP_PX = 8;
/** Gap kept between a moved disc (or its handle) and the screen edge. */
const EDGE_PX = 8;

type Point = { x: number; y: number };

/** Keep the disc, with its handle to the left, fully on screen. */
function clampDisc(p: Point): Point {
  const minX = EDGE_PX + HANDLE_W_PX + HANDLE_GAP_PX;
  const maxX = Math.max(minX, window.innerWidth - LAUNCHER_SIZE_PX - EDGE_PX);
  const maxY = Math.max(EDGE_PX, window.innerHeight - LAUNCHER_SIZE_PX - EDGE_PX);
  return {
    x: Math.min(Math.max(p.x, minX), maxX),
    y: Math.min(Math.max(p.y, EDGE_PX), maxY),
  };
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

export const LAUNCHER_SIZE_PX = 56;

/**
 * The avoid contract. Any page control that lives in the launcher's corner
 * (a bottom-slot composer or recorder, a FAB, a sticky action bar) marks its
 * root with `data-alfie-avoid`, and the launcher lifts `AVOID_GAP_PX` above
 * the highest such element that shares its column and its vertical band —
 * instead of sitting on top of it.
 *
 * Returns the distance from the viewport bottom to that element's top edge,
 * or `null` when nothing on this page is in the way. A marked element that is
 * hidden, off in the page's left half, or sitting mid-page does not count, so
 * a centred form's sticky bar only lifts the launcher on the narrow viewports
 * where the two actually meet.
 */
export function useAvoidOffset(inset: number): number | null {
  const [offset, setOffset] = useState<number | null>(null);

  useEffect(() => {
    let observed: Element[] = [];
    let observer: ResizeObserver | undefined;

    // getBoundingClientRect, never the observer's `contentRect`: contentRect
    // is the CONTENT box and drops the element's own padding, so the two
    // paths would disagree and the disc would jump on first resize.
    const measure = () => {
      const columnLeft = window.innerWidth - inset - LAUNCHER_SIZE_PX;
      const bandTop = window.innerHeight - inset - LAUNCHER_SIZE_PX - AVOID_GAP_PX;
      let top: number | null = null;
      for (const el of Array.from(document.querySelectorAll('[data-alfie-avoid]'))) {
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) continue;
        if (rect.right <= columnLeft) continue;
        if (rect.bottom <= bandTop || rect.top >= window.innerHeight) continue;
        top = top === null ? rect.top : Math.min(top, rect.top);
      }
      setOffset(top === null ? null : window.innerHeight - top);
    };

    const sync = () => {
      const els = Array.from(document.querySelectorAll('[data-alfie-avoid]'));
      const same = els.length === observed.length && els.every((el, i) => el === observed[i]);
      if (!same) {
        observer?.disconnect();
        observer = undefined;
        observed = els;
        if (els.length > 0 && typeof ResizeObserver !== 'undefined') {
          observer = new ResizeObserver(measure);
          els.forEach((el) => observer?.observe(el));
        }
      }
      // Nothing to avoid on this page (or the composer unmounted): back to the
      // normal inset. Latching a fixed fallback here once parked the disc 96px
      // up with nothing under it.
      measure();
    };

    sync();
    // Marked elements mount with their page (a conversation's composer lands
    // long after this effect) and unmount when the user switches tabs. A
    // cheap poll keeps the disc parked correctly through both.
    const timer = window.setInterval(sync, 250);
    window.addEventListener('resize', measure);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [inset]);

  return offset;
}

/**
 * Where the launcher's bottom edge sits, in px above the viewport bottom
 * (before the mobile `--safe-bottom` term): the default inset, or 12px above
 * the highest avoided element. The widget root calls this ONCE and hands the
 * value to both the launcher and the panel, so the panel lifts with the disc
 * and never lands on top of it.
 */
export function useLauncherBottom(isMobile: boolean): number {
  const inset = isMobile ? MOBILE_INSET_PX : DESKTOP_INSET_PX;
  const avoidOffset = useAvoidOffset(inset);
  return avoidOffset === null ? inset : avoidOffset + AVOID_GAP_PX;
}

export function AlfieLauncher({
  isOpen,
  onToggle,
  bottomPx,
  isAnswering = false,
  hasNewReply = false,
  positionKey,
}: AlfieLauncherProps) {
  const isMobile = useIsMobile();
  const reducedMotion = usePrefersReducedMotion();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);

  /*
   * MOVING THE DISC. Hold it (touch, MOVE_HOLD_MS) or rest the mouse on it
   * (MOVE_HOVER_MS) and a glass grip shows to its left; dragging the grip
   * carries the disc anywhere on screen. The spot lives only in this state:
   * a page change or a reload puts the disc back in its corner. While moved,
   * the disc ignores `bottomPx` (it no longer lifts over a composer) and sits
   * on top of the page at its usual z-[52]. The panel still opens in its own
   * place — only the disc moves.
   */
  const [moved, setMoved] = useState<Point | null>(null);
  const [handleAt, setHandleAt] = useState<Point | null>(null);
  const holdTimer = useRef<number | null>(null);
  const idleTimer = useRef<number | null>(null);
  const holdStart = useRef<Point | null>(null);
  /** The hold fired, so the click that ends the press must not open Alfie. */
  const swallowClick = useRef(false);
  const drag = useRef<{ pointerId: number; from: Point; disc: Point } | null>(null);

  const clearHold = () => {
    if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
    holdTimer.current = null;
    holdStart.current = null;
  };
  const hideHandle = () => {
    if (idleTimer.current !== null) window.clearTimeout(idleTimer.current);
    idleTimer.current = null;
    drag.current = null;
    setHandleAt(null);
  };
  const armIdle = () => {
    if (idleTimer.current !== null) window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(hideHandle, MOVE_IDLE_MS);
  };
  const showHandle = () => {
    holdTimer.current = null;
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHandleAt({ x: rect.left, y: rect.top });
    armIdle();
  };

  useEffect(() => {
    setMoved(null);
    hideHandle();
    clearHold();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionKey]);

  useEffect(() => () => {
    clearHold();
    if (idleTimer.current !== null) window.clearTimeout(idleTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A tap or click anywhere but the disc or the grip puts the grip away.
  useEffect(() => {
    if (!handleAt) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || handleRef.current?.contains(t)) return;
      hideHandle();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [handleAt]);

  // A moved disc stays on screen when the window shrinks or the phone turns.
  useEffect(() => {
    if (!moved) return;
    const onResize = () => setMoved((p) => (p ? clampDisc(p) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [moved]);

  const onPointerDown = (e: React.PointerEvent) => {
    // A fresh press: some phones never send the click that ends a long press,
    // so a swallow left over from the last hold must not eat this tap.
    swallowClick.current = false;
    if (e.pointerType === 'mouse') return;
    clearHold();
    holdStart.current = { x: e.clientX, y: e.clientY };
    holdTimer.current = window.setTimeout(() => {
      swallowClick.current = true;
      showHandle();
    }, MOVE_HOLD_MS);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const start = holdStart.current;
    if (!start || holdTimer.current === null) return;
    if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > HOLD_SLOP_PX) clearHold();
  };
  const onPointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse' || handleAt) return;
    clearHold();
    holdTimer.current = window.setTimeout(showHandle, MOVE_HOVER_MS);
  };
  const onPointerLeave = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') clearHold();
  };
  const onClick = () => {
    if (swallowClick.current) {
      swallowClick.current = false;
      return;
    }
    clearHold();
    onToggle();
  };

  const onHandleDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { pointerId: e.pointerId, from: { x: e.clientX, y: e.clientY }, disc: { x: rect.left, y: rect.top } };
    if (idleTimer.current !== null) window.clearTimeout(idleTimer.current);
  };
  const onHandleMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const next = clampDisc({ x: d.disc.x + e.clientX - d.from.x, y: d.disc.y + e.clientY - d.from.y });
    setMoved(next);
    setHandleAt(next);
  };
  const onHandleUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    // Let go without moving: leave the grip up for another go until it idles out.
    if (e.clientX === d.from.x && e.clientY === d.from.y) {
      drag.current = null;
      armIdle();
      return;
    }
    hideHandle();
  };

  // Mobile opens a full-height sheet — a launcher on top of it is noise.
  if (isMobile && isOpen) return null;

  const inset = isMobile ? MOBILE_INSET_PX : DESKTOP_INSET_PX;
  // --safe-bottom is env() in a browser tab and 0 in the installed PWA, so the
  // disc clears the home indicator without double-insetting (globals.css).
  const bottom = isMobile ? `calc(${bottomPx}px + var(--safe-bottom))` : `${bottomPx}px`;

  const showClose = isOpen && !isMobile;
  const transition = reducedMotion ? 'none' : 'opacity 200ms ease, transform 200ms ease';
  // Neither nudge ever shows on an open widget: the answer is already on
  // screen, and on mobile the launcher is unmounted anyway.
  const showRing = isAnswering && !isOpen;
  const showDot = hasNewReply && !isOpen;
  const position = moved
    ? { left: `${moved.x}px`, top: `${moved.y}px` }
    : { right: `${inset}px`, bottom };

  return (
    <>
    <button
      ref={buttonRef}
      type="button"
      data-alfie-launcher
      data-alfie-moved={moved ? '' : undefined}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={clearHold}
      onPointerCancel={clearHold}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      // A long press must not raise the phone's own callout or text menu.
      onContextMenu={(e) => e.preventDefault()}
      aria-label={showDot ? 'Ask Alfie, new reply' : 'Ask Alfie'}
      aria-expanded={isOpen}
      style={position}
      // z-[52]: see LAYERING in the file header.
      className="group fixed z-[52] select-none [-webkit-touch-callout:none] flex h-14 w-14 items-center justify-center rounded-full transition-transform duration-200 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      {/* "Answering while closed": a 72px halo breathing behind the disc. It is
          centred with its own transform and sits INSIDE the 56px button box, so
          the 8px overhang never moves the disc or its right/bottom insets. */}
      {showRing && (
        <span
          aria-hidden
          data-alfie-answering
          className={`pointer-events-none absolute left-1/2 top-1/2 h-[72px] w-[72px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary opacity-[0.16] ${
            reducedMotion ? '' : 'animate-[alfie-launcher-pulse_2s_ease-in-out_infinite]'
          }`}
        />
      )}

      {/* The disc is a child, not the button's own background, so the halo
          above can paint BEHIND it. */}
      <span
        aria-hidden
        data-alfie-disc
        className="pointer-events-none absolute inset-0 rounded-full bg-primary shadow-[0_4px_16px_rgba(0,0,0,0.10),0_12px_32px_rgba(0,0,0,0.10)] transition-shadow duration-200 group-hover:shadow-[0_6px_20px_rgba(0,0,0,0.14),0_16px_40px_rgba(0,0,0,0.14)] motion-reduce:transition-none"
      />

      {/* Hover darkens the disc a step without touching the mark. A fixed
          black wash (not the foreground token) so the press reads the same
          way in both themes. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full bg-black/0 transition-colors duration-200 group-hover:bg-black/[0.08] motion-reduce:transition-none"
      />

      {/* Hover/keyboard-focus label. `title` is deliberately absent: the native
          tooltip would stack a second, unstyled bubble on top of this one. */}
      {!isMobile && !isOpen && !handleAt && (
        <span
          aria-hidden
          style={GLASS_BACKDROP}
          className="pointer-events-none absolute right-[calc(100%+12px)] flex h-8 items-center gap-2 whitespace-nowrap rounded-md border border-[hsl(var(--glass-border))] bg-[hsl(var(--glass-bg))] px-3 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none"
        >
          <span className="text-xs font-medium leading-4 text-foreground">Ask Alfie</span>
          <span className="text-[11px] leading-[14px] text-muted-foreground">{SHORTCUT_LABEL}</span>
        </span>
      )}

      {/* Both marks are always mounted and cross-fade in place, so the disc
          never reflows mid-transition. */}
      <span className="relative flex h-[34px] w-[34px] items-center justify-center">
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transition,
            opacity: showClose ? 0 : 1,
            transform: showClose ? 'rotate(-90deg) scale(0.7)' : 'none',
          }}
        >
          <HearthstoneIcon size={34} variant="mono-light" />
        </span>
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center text-[#EDEBE8]"
          style={{
            transition,
            opacity: showClose ? 1 : 0,
            transform: showClose ? 'none' : 'rotate(90deg) scale(0.7)',
          }}
        >
          <X className="h-[22px] w-[22px]" strokeWidth={2} />
        </span>
      </span>

      {/* "Answer ready": a 14px dot flush with the disc's top-right corner.
          border-box keeps the 2px ring inside the 14px, so the dot cannot
          nudge the disc. Foreground on a background ring keeps it readable
          over the terracotta in both themes. The state is announced through
          the button's aria-label, so the dot itself is decorative. */}
      {showDot && (
        <span
          aria-hidden
          data-alfie-new-reply
          className="pointer-events-none absolute right-0 top-0 h-3.5 w-3.5 rounded-full border-2 border-background bg-foreground"
        />
      )}
    </button>
    {handleAt && (
      <div
        ref={handleRef}
        role="button"
        aria-label="Move Alfie"
        data-alfie-move-handle
        onPointerDown={onHandleDown}
        onPointerMove={onHandleMove}
        onPointerUp={onHandleUp}
        onPointerCancel={onHandleUp}
        style={{
          ...GLASS_BACKDROP,
          left: `${handleAt.x - HANDLE_GAP_PX - HANDLE_W_PX}px`,
          top: `${handleAt.y + (LAUNCHER_SIZE_PX - HANDLE_H_PX) / 2}px`,
          width: `${HANDLE_W_PX}px`,
          height: `${HANDLE_H_PX}px`,
        }}
        // touch-none: the drag must not scroll the page underneath.
        className="fixed z-[52] flex cursor-grab touch-none select-none items-center justify-center rounded-full border border-[hsl(var(--glass-border))] bg-[hsl(var(--glass-bg))] shadow-[0_4px_16px_rgba(0,0,0,0.10)] active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
    )}
    </>
  );
}

export default AlfieLauncher;
